import React, { useState, useEffect } from 'react';
import { useUser, useSession } from '@clerk/clerk-react';
import { getEnvApiBaseUrl } from '@/config/env';

/**
 * Clerk Diagnostics Page
 * 
 * Development-only page that displays Clerk configuration status,
 * session information, and provides backend connectivity testing.
 * 
 * Accessible at /__clerk/diagnostics/ui in development mode only.
 */

interface BackendDiagnostics {
  env: string;
  timestamp: string;
  clerk: {
    hasSecretKey: boolean;
    secretKeyPrefix: string;
    hasPublishableKey: boolean;
    publishableKeyPrefix: string;
    jwtIssuer: string;
    jwksUrl: string;
    environment: string;
  };
  app: {
    baseUrl: string;
    port: string;
    corsOrigins: string;
  };
  database: {
    connected: boolean;
    urlPrefix: string;
  };
}

export default function ClerkDiagnostics() {
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const [backendDiagnostics, setBackendDiagnostics] = useState<BackendDiagnostics | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const apiBaseUrl = getEnvApiBaseUrl();

  // Only show restricted message in production
  const isProd = import.meta.env.PROD;

  const pingBackend = async () => {
    setIsLoading(true);
    setBackendError(null);
    
    try {
      const response = await fetch(`${apiBaseUrl}/__clerk/diagnostics`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setBackendDiagnostics(data);
    } catch (error) {
      setBackendError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    pingBackend();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">🔧 Clerk Diagnostics</h1>
          {isProd && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
              <h2 className="text-lg font-semibold text-red-700 mb-2">Access Limited</h2>
              <p className="text-red-600 text-sm">Diagnostics are intended for development environments.</p>
            </div>
          )}
          
          {/* Frontend Configuration */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Frontend Configuration</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Publishable Key:</span>
                <span className="font-mono text-sm">
                  {publishableKey ? `${publishableKey.slice(0, 15)}...` : '❌ Not set'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">API Base URL:</span>
                <span className="font-mono text-sm">{apiBaseUrl}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Current Hostname:</span>
                <span className="font-mono text-sm">{window.location.hostname}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Current Origin:</span>
                <span className="font-mono text-sm">{window.location.origin}</span>
              </div>
            </div>
          </div>

          {/* Clerk Session Status */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Clerk Session Status</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">User Loaded:</span>
                <span className={userLoaded ? 'text-green-600' : 'text-yellow-600'}>
                  {userLoaded ? '✅ Yes' : '⏳ Loading...'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Session Loaded:</span>
                <span className={sessionLoaded ? 'text-green-600' : 'text-yellow-600'}>
                  {sessionLoaded ? '✅ Yes' : '⏳ Loading...'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">User Signed In:</span>
                <span className={user ? 'text-green-600' : 'text-red-600'}>
                  {user ? '✅ Yes' : '❌ No'}
                </span>
              </div>
              {user && (
                <>
                  <div className="flex justify-between">
                    <span className="font-medium">User ID:</span>
                    <span className="font-mono text-sm">{user.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Email:</span>
                    <span className="font-mono text-sm">{user.primaryEmailAddress?.emailAddress || 'Not set'}</span>
                  </div>
                </>
              )}
              {session && (
                <div className="flex justify-between">
                  <span className="font-medium">Session ID:</span>
                  <span className="font-mono text-sm">{session.id}</span>
                </div>
              )}
            </div>
          </div>

          {/* Backend Connectivity */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Backend Connectivity</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <button
                onClick={pingBackend}
                disabled={isLoading}
                className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? '⏳ Pinging Backend...' : '🔗 Ping Backend'}
              </button>
              
              {backendError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                  <p className="text-red-700 font-medium">❌ Backend Error:</p>
                  <p className="text-red-600 text-sm">{backendError}</p>
                </div>
              )}

              {backendDiagnostics && (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Backend Clerk Configuration:</h3>
                    <div className="bg-white rounded p-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Secret Key:</span>
                        <span className="font-mono">{backendDiagnostics.clerk.secretKeyPrefix}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Publishable Key:</span>
                        <span className="font-mono">{backendDiagnostics.clerk.publishableKeyPrefix}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>JWT Issuer:</span>
                        <span className="font-mono text-xs">{backendDiagnostics.clerk.jwtIssuer}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>JWKS URL:</span>
                        <span className="font-mono text-xs">{backendDiagnostics.clerk.jwksUrl}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Environment:</span>
                        <span>{backendDiagnostics.clerk.environment}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Backend App Configuration:</h3>
                    <div className="bg-white rounded p-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Base URL:</span>
                        <span>{backendDiagnostics.app.baseUrl}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Port:</span>
                        <span>{backendDiagnostics.app.port}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>CORS Origins:</span>
                        <span className="font-mono text-xs">{backendDiagnostics.app.corsOrigins}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Database Status:</h3>
                    <div className="bg-white rounded p-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Connected:</span>
                        <span className={backendDiagnostics.database.connected ? 'text-green-600' : 'text-red-600'}>
                          {backendDiagnostics.database.connected ? '✅ Yes' : '❌ No'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>URL Prefix:</span>
                        <span className="font-mono text-xs">{backendDiagnostics.database.urlPrefix}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="flex space-x-4">
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                🏠 Go Home
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                🔄 Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
